/**
 * 全站路由配置（vue-router 3.x 动态路由方案）
 *
 * meta参数说明
 * keepAlive是否缓冲页面
 * isTab是否加入到tag导航
 * isAuth是否需要授权
 *
 * 思路：
 *   1. 启动时只注册静态路由（登录页、数据生成页、首页壳子）
 *   2. 登录接口返回 order_list 后存进 localStorage
 *   3. 守卫发现「已登录但动态路由还没挂载」时，用 addRoutes 注入后端菜单路由，
 *      然后 next({ ...to, replace: true }) 重新走一次导航（否则本次匹配用的还是旧路由表）
 *   4. 退出登录时 resetRouter() 换掉 matcher，下次登录按新账号重新挂载
 */
import Vue from 'vue';
import VueRouter from 'vue-router';
import PageRouter from './page/'; // 静态路由
import Layout from '@/page/index/'; // 布局壳子，动态路由的父组件
import { getToken, removeCookie } from '@/util/auth';
import {
  readMenu,
  clearMenu,
  generateDynamicRoutes,
  collectAccessiblePaths,
} from './menu';

Vue.use(VueRouter);

// 不需要登录就能访问的页面
const WHITE_LIST = ['/login', '/create'];
// 兜底首页（访问无权限/不存在的路径时回这里）
const HOME_PATH = '/index';

// 创建路由实例：只带静态路由
export const createRouter = () => new VueRouter({
  routes: PageRouter,
});

const Router = createRouter(); // 获得 route 实例

// 动态路由是否已挂载
let routesMounted = false;
// 已挂载的动态路由，用于路径校验
let dynamicRoutes = [];
// 缓存所有可访问路径，避免每次导航都递归一遍
let accessiblePaths = [];

/** 重新计算可访问路径白名单 */
function refreshAccessiblePaths() {
  accessiblePaths = collectAccessiblePaths([...PageRouter, ...dynamicRoutes]);
}

/**
 * 把后端菜单挂载到 router 上
 * @returns {Array} 挂载的动态路由
 */
export function mountDynamicRoutes() {
  const menu = readMenu();
  dynamicRoutes = generateDynamicRoutes(menu, Layout);
  if (dynamicRoutes.length) {
    Router.addRoutes(dynamicRoutes);
  } else {
    console.warn('[router] 没有从 localStorage(order_list) 读到可用的后端菜单，动态路由为空');
  }
  routesMounted = true;
  refreshAccessiblePaths();
  return dynamicRoutes;
}

/**
 * 重置路由：清空动态路由，恢复到只有静态路由的状态
 * vue-router 3.x 没有 removeRoute，只能替换 matcher
 * 退出登录 / 切换账号时必须调用，否则上一个账号的路由会残留
 */
export function resetRouter() {
  Router.matcher = createRouter().matcher;
  routesMounted = false;
  dynamicRoutes = [];
  accessiblePaths = [];
}

/** 清理登录态（不触发跳转，跳转交给守卫或调用方） */
export function clearLoginState() {
  removeCookie('token');
  clearMenu();
  resetRouter();
}

refreshAccessiblePaths();

Router.beforeEach((to, from, next) => {
  // 用来去除跳转页面 表格存在show-overflow-tooltip 不消失问题
  const nodeList = document.querySelectorAll('.el-tooltip__popper');
  nodeList.forEach(item => {
    item.style.display = 'none';
  });

  const hasToken = getToken('token');

  // --- 情况 B：未登录 ---
  if (!hasToken) {
    // 动态路由可能还挂着上一个账号的，顺手清掉
    if (routesMounted) resetRouter();
    if (WHITE_LIST.includes(to.path)) return next();
    // 未登录访问任何业务页面，一律拦截去登录页
    return next('/login');
  }

  // --- 情况 A：已登录 ---
  // 已登录还去登录页，直接回首页
  if (to.path === '/login') return next(HOME_PATH);

  // 关键：登录后第一次导航，先把后端菜单挂载上，再重新进入目标页
  if (!routesMounted) {
    mountDynamicRoutes();
    // replace: true 避免在历史里留下一个匹配失败的记录
    return next({ ...to, replace: true });
  }

  // 路径校验：静态页、白名单、后端菜单页放行，其余回首页
  if (accessiblePaths.includes(to.path) || WHITE_LIST.includes(to.path)) {
    return next();
  }
  return next(HOME_PATH);
});

export default Router;



// WEBPACK FOOTER //
// ./src/router/router.js